import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PhoneMissed, PhoneOutgoing, Clock } from "lucide-react";
import MissedCallReturnModal from "@/components/MissedCallReturnModal";

/**
 * Shows unresolved missed inbound calls that reached nobody (out-of-hours or
 * everyone offline). Both the rep and the owner can mark them "answered back"
 * with notes + outcome, which moves the call into normal call history.
 */
export default function MissedCallsPanel({ L, testidPrefix = "missed" }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returning, setReturning] = useState(null); // call being marked answered

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calls/missed");
      setCalls(data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMin = Math.round((now - d) / 60000);
      if (diffMin < 1) return L ? "μόλις τώρα" : "just now";
      if (diffMin < 60) return `${diffMin}${L ? " λ" : "m"}`;
      if (diffMin < 1440) return `${Math.round(diffMin / 60)}${L ? " ω" : "h"}`;
      return d.toLocaleDateString(L ? "el-GR" : "en-GB");
    } catch { return iso; }
  };

  if (!loading && calls.length === 0) return null; // hide widget when empty

  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`${testidPrefix}-panel`}>
      <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-red-50/40">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
            <PhoneMissed className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-red-700">
              {L ? "Αναπάντητες κλήσεις" : "Missed calls"}
            </div>
            <div className="text-sm font-heading font-bold text-slate-900">
              {calls.length} {L ? "χρειάζονται επιστροφή" : "need a call-back"}
            </div>
          </div>
        </div>
        <button
          onClick={load}
          data-testid={`${testidPrefix}-refresh`}
          className="text-xs font-semibold text-slate-500 hover:text-slate-900"
        >
          {L ? "Ανανέωση" : "Refresh"}
        </button>
      </header>

      {loading ? (
        <div className="p-6 text-center text-sm text-slate-500">{L ? "Φόρτωση..." : "Loading..."}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {calls.map((c) => (
            <li key={c.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50" data-testid={`${testidPrefix}-item-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-slate-900" data-testid={`${testidPrefix}-number-${c.id}`}>
                    {c.missed_from_number || c.phone_number_called || "—"}
                  </span>
                  {c.rep_name && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      {c.rep_name}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                  <Clock className="w-3 h-3" /> {fmtTime(c.date)}
                  <span>· {L ? "εισερχόμενη" : "inbound"}</span>
                </div>
              </div>
              <button
                onClick={() => setReturning(c)}
                data-testid={`${testidPrefix}-mark-${c.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0052CC] hover:bg-[#0043A8] text-white text-xs font-semibold rounded-md shadow-sm"
              >
                <PhoneOutgoing className="w-3.5 h-3.5" /> {L ? "Απαντήθηκε" : "Called back"}
              </button>
            </li>
          ))}
        </ul>
      )}

      {returning && (
        <MissedCallReturnModal
          call={returning}
          L={L}
          onClose={() => setReturning(null)}
          onDone={() => { setReturning(null); load(); }}
        />
      )}
    </section>
  );
}
